import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import Modal from '../ui/Modal';
import Input from '../ui/Input';
import Button from '../ui/Button';
import Spinner from '../ui/Spinner';
import { useAuth } from '../../context/AuthContext';
import { parseApiError } from '../../utils/apiError';
import { isValidEmail } from '../../utils/email';

const EMPTY = { fName: '', lName: '', email: '', phone: '', password: '', password2: '' };

// Guest registration only. Role is assigned server-side by email membership —
// there is no role field here or anywhere in the UI (admin registration is
// impossible by design).
export default function RegisterModal({ isOpen, onClose, onSwitchToLogin }) {
  const { register } = useAuth();

  const [values, setValues] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [registered, setRegistered] = useState(null); // register() response, once submitted successfully

  const setField = (key) => (e) => setValues((v) => ({ ...v, [key]: e.target.value }));

  const close = () => {
    setValues(EMPTY);
    setSubmitting(false);
    setFormError(null);
    setFieldErrors({});
    setRegistered(null);
    onClose();
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError(null);
    setFieldErrors({});

    const email = values.email.trim();
    if (!isValidEmail(email)) {
      setFieldErrors({ email: 'Please enter a valid email address' });
      setSubmitting(false);
      return;
    }

    try {
      const data = await register({
        fName: values.fName.trim(),
        lName: values.lName.trim(),
        email,
        phone: values.phone.trim(),
        password: values.password,
        password2: values.password2,
      });
      setRegistered(data || {});
      setSubmitting(false);
    } catch (err) {
      const { formError: fe, fieldErrors: fld } = parseApiError(err);
      setFormError(fe);
      setFieldErrors(fld);
      setSubmitting(false);
    }
  };

  const devToken = registered?.devVerificationToken;

  return (
    <Modal isOpen={isOpen} onClose={close} title={registered ? 'Account created' : 'Create your account'}>
      {registered ? (
        <div className="ss-form">
          <p className="ss-form__success">
            We've sent a verification link to your email. Please verify your
            email, then log in to start shopping.
          </p>
          {devToken && (
            <p className="ss-authswitch">
              Dev shortcut:{' '}
              <Link to={`/verify-email?token=${encodeURIComponent(devToken)}`} onClick={close}>
                open verify page
              </Link>
            </p>
          )}
          <Button onClick={onSwitchToLogin}>Go to log in</Button>
        </div>
      ) : (
        <form className="ss-form" onSubmit={onSubmit} noValidate>
          {formError && <p className="ss-form__error" role="alert">{formError}</p>}

          <div className="ss-form__row">
            <Input label="First name" value={values.fName} onChange={setField('fName')} error={fieldErrors.fName} autoComplete="given-name" required />
            <Input label="Last name" value={values.lName} onChange={setField('lName')} error={fieldErrors.lName} autoComplete="family-name" required />
          </div>
          <Input label="Email" type="email" value={values.email} onChange={setField('email')} error={fieldErrors.email} autoComplete="email" required />
          <Input label="Phone" type="tel" value={values.phone} onChange={setField('phone')} error={fieldErrors.phone} autoComplete="tel" required />
          <div className="ss-form__row">
            <Input label="Password" type="password" value={values.password} onChange={setField('password')} error={fieldErrors.password} autoComplete="new-password" required />
            <Input label="Confirm password" type="password" value={values.password2} onChange={setField('password2')} error={fieldErrors.password2} autoComplete="new-password" required />
          </div>

          <div className="ss-form__actions">
            <Button type="submit" disabled={submitting}>
              {submitting ? <Spinner label="Creating account" /> : 'Create account'}
            </Button>
            <p className="ss-authswitch">
              Already have an account?{' '}
              <button type="button" className="ss-linkbtn" onClick={onSwitchToLogin}>
                Log in
              </button>
            </p>
          </div>
        </form>
      )}
    </Modal>
  );
}
